// src/api/deep-signal-api.ts
// API helpers for the Deep Signal module.

import { apiFetch } from "./api-fetch";
import type { RunCompleteness } from "./run-completeness";
import type { RunSeries, SeriesRebaseline } from "./series-rebaseline";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DSVariable {
  id: string;
  claim: string;
}

export interface DSModelBreakdown {
  total_responses: number;
  mention_rate: number;
  top5_rate: number;
  primary_rec_rate: number;
  number_one_rate: number;
}

export interface DSVariableIntelligence {
  by_model: Record<string, Record<string, number>>; // model -> rv_id -> 0–1 score
  overall: Record<string, number>;                   // rv_id -> 0–1 score
}

export interface DSInsightCard {
  id: number;
  card_type: string;
  type_color: string;
  type_bg: string;
  accent_color: string;
  models: string[];
  headline: string;
  snippet: string;
  full_quote: string;
  key_phrase: string;
  source: string;
  rv_id: string;
}

export interface DSPersonaPerModel {
  primary_rec_rate: number;
}

export interface DSPersona {
  name: string;
  role: string;
  decisionFrame: string;
  accentColor: string;
  mention_rate: number;
  top5_rate: number;
  primary_rec_rate: number;
  per_model: Record<string, DSPersonaPerModel>;
  variable_scores: Record<string, number>;
}

export interface DSRun {
  id: number;
  run_label: string | null;
  run_sequence_number: number | null;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  // The /runs list route returns created_at (always set); the per-run payload
  // route does not. Optional so both shapes satisfy the type.
  created_at?: string | null;
  scored_count?: number;
  // The server's series verdict: whether this run plots as a client-facing
  // Performance point. It already accounts for an admin exclusion, an admin
  // force-include, a pre-flight label, AND the scored-row floor, so it is the
  // ONLY field the series builder filters on. Present on the /runs list route;
  // absent on the per-run payload route.
  series_eligible?: boolean;
  // Why it does not plot: "admin_excluded" | "preflight_label" |
  // "below_scored_floor". null when it does.
  series_exclusion_reason?: string | null;
  // The admin's standing decision: "auto" (rules decide) | "exclude" | "include".
  series_inclusion?: "auto" | "include" | "exclude";
  // Scored rows an "auto" run needs to clear, for this customer.
  series_scored_floor?: number;
  // Legacy boolean view of series_inclusion === "exclude". Kept as the fallback
  // for a bundle talking to a backend that predates series_eligible; prefer
  // series_eligible, which the boolean alone cannot express (it says nothing
  // about the scored-row floor, which is how a 6-row run reached the chart).
  excluded_from_series?: boolean;
  // Stored report headline (baseline_report_json.headline) — the same numbers
  // Compare Runs reads. Present on the /runs list route once a run's report is
  // generated; used for the dashboard's "vs baseline" delta so it compares
  // against the original scored pass, not the live (possibly re-scored) rows.
  headline_metrics?: {
    mention_rate: number;
    top5_rate: number;
    primary_rec_rate: number;
    number_one_rate: number;
  } | null;
  // Incomplete-run verdict (excluded + failed calls over the threshold, by
  // lane). On both the /runs list and the per-run payload's `run`.
  completeness?: RunCompleteness | null;
  // Series segment (battery version, opening rebaseline, new-baseline flag).
  // On both routes. Absent from an older backend = segment 0.
  series?: RunSeries | null;
  // Truth set, part 4: drift and the flagged gaps of this read, checked
  // against one confirmed truth-set version. Per-run payload only; null until
  // the read has a complete check. Clients get shares only (no counts).
  truth_check?: ReadTruthCheck | null;
}

export interface DriftCell {
  share: number | null;
  margin: number | null;
  present?: number;
  answers?: number;
}

export interface FlaggedGap {
  fact_id: number | null;
  attribute: string;
  attribute_label?: string;
  attribute_noun?: string;
  // Part 6: the gap's action status, when the action list has one for it.
  status?: { status: "new" | "in_action" | "fixed" | "persisting"; label: string; reads_elapsed?: number } | null;
  subject_type: string;
  subject_name: string;
  qualifier: string;
  truth_value: string | null;
  answer_value: string;
  share: number | null;
  answers?: number;
  answers_checked?: number;
  lanes: { lane: string; label: string; answers?: number }[];
  examples: { lane: string; lane_label: string; sentence: string }[];
  // Part 5: third-party pages that carry the wrong value.
  carriers?: { url: string; title?: string | null; lanes: string[]; quote?: string | null; own_site?: boolean }[];
}

export interface CandidateFact {
  attribute: string;
  attribute_label?: string;
  subject_type: string;
  subject_name: string;
  qualifier: string;
  value: string;
  answers: number;
  lanes: { lane: string; label: string }[];
  example: string;
}

export interface ReadTruthCheck {
  version_id: number;
  version_number: number | null;
  checked_at: string | null;
  drift: {
    overall: DriftCell;
    by_lane: Record<string, DriftCell>;
    blend_lanes: string[];
    answers_checked?: number;
    answers_failed?: number;
  };
  comparison: null | {
    comparable: boolean;
    previous_run_id: number | null;
    previous_label: string | null;
    marker?: string;
    previous_share?: number;
    delta?: number;
    band?: number;
    moved?: boolean;
  };
  gaps: FlaggedGap[];
  candidates?: CandidateFact[];
  check?: { id: number; model: string; cost_usd: number | null; answers_failed: number | null };
}

export interface DSCustomer {
  id: number;
  brand_name: string;
  competitors: string[];
}

export interface DSCompetitorBrand {
  name: string;
  // null = unmeasured (standard onboarding captures competitor names only, no rates).
  mention_rate: number | null;
  top5_rate: number | null;
  primary_rec_rate: number | null;
  per_model: Record<string, number | null>; // model key -> mention_rate
}

export interface DSCompetitiveFocalBrand {
  // null = unmeasured under the table's instrument for that column (e.g.
  // primary before the run is judged). The focal row shares the competitor
  // rows' instruments; the hero funnel elsewhere stays on the judged focal
  // instrument and may differ by about a point.
  mention_rate: number | null;
  top5_rate: number | null;
  primary_rec_rate: number | null;
  per_model: Record<string, {
    mention_rate: number | null;
    top5_rate?: number | null;
    primary_rec_rate?: number | null;
  }>;
}

// Source of each Competitive column, the same for every row of the table:
//   "judged"    - the competitive scorer's per-response stamps
//                 (coverage-gated; one dataset, two scorers)
//   "benchmark" - stored rates migrated with the brand (legacy path)
//   null        - unmeasured: the frontend hides the column entirely
export type DSCompetitiveInstrument = 'judged' | 'benchmark' | null;

export interface DSCompetitiveInstruments {
  mention: DSCompetitiveInstrument;
  top5: DSCompetitiveInstrument;
  primary: DSCompetitiveInstrument;
}

export interface BlueprintIntervention {
  id: string;
  title: string;
  description: string;
  effort: 'LOW' | 'MEDIUM' | 'HIGH';
  signal: string;
  /** The beliefs (reasoning variable ids) the item targets: its drafts are
   *  the content written for those beliefs' actions (#132). */
  target_variables?: string[];
}

export interface BlueprintWave {
  number: number;
  title: string;
  subtitle: string;
  velocity_label: string;
  interventions: BlueprintIntervention[];
}

export interface Blueprint {
  produced_from_run_id?: number;
  produced_at?: string;
  waves: BlueprintWave[];
}

// Key Insight card. Owned here so the backend can populate `key_insights` on
// the run payload later with no frontend change. Re-exported from the
// deep-signal component `types.ts` for existing imports.
export interface KeyInsight {
  id: string;
  delta: string;
  deltaType: 'positive' | 'negative' | 'neutral' | 'info';
  title: string;
  summary: string;
  body: string;
  confidence: number;
  sources: string;
  timeAgo: string;
  aiReasoning: string;
}

// Complete per-model-per-variable evidence (not capped, not pattern-filtered).
// Keyed by model API key (openai, claude, gemini, perplexity), then rv_id.
// null means no evidence row exists for that model/variable pair.
export type DSCellEvidence = Record<string, Record<string, {
  evidence: string;
  query: string;
  score: number;
} | null>>;

export interface DeepSignalData {
  customer: DSCustomer;
  run: DSRun & { created_at?: string };
  variables: DSVariable[];
  variable_intelligence: DSVariableIntelligence;
  model_breakdown: Record<string, DSModelBreakdown>;
  /**
   * Which lanes in `model_breakdown` count toward THIS CLIENT's blended and
   * headline numbers, decided by the server per client.
   *
   * A lane added to a client after their baseline run still renders as its own
   * discrete lane everywhere; it is only kept out of blended numbers. The
   * browser cannot work this out for itself: the catalog compiled into the
   * bundle knows lane ids, not which client is being rendered or what was in
   * their baseline.
   *
   * OPTIONAL on purpose. An older API build, or a cached payload, omits it.
   * Callers must fall back to the catalog rule rather than treating an absent
   * key as "nothing is eligible", which would render every headline as zero.
   */
  blend_eligible_models?: string[];
  /** The complement of the above, for the "what was left out" affordance. */
  blend_excluded_models?: string[];
  what_ai_says: DSInsightCard[];
  cell_evidence: DSCellEvidence;
  personas: DSPersona[];
  competitive: {
    focal_brand: DSCompetitiveFocalBrand;
    competitor_brands: DSCompetitorBrand[];
    // Absent on older payloads: the frontend then renders all columns
    // (legacy behavior).
    instruments?: DSCompetitiveInstruments;
  };
  blueprint: Blueprint | null;
  // Managed-service content. Optional today: the backend does not return these
  // yet, so the frontend falls back to the per-customer content config
  // (deep-signal-content.ts). When the editor backend ships, populating these
  // makes them win over the config with no further frontend change.
  // - key_insights: per-run, editable/auto-generated insight cards.
  // - blueprint_enabled: per-customer toggle for the Blueprint tab.
  key_insights?: KeyInsight[] | null;
  // True when `key_insights` is the admin-curated override
  // (customer_curated_content) rather than the generated/config default. Gates
  // the "Reset to default" affordance on the Key Insights editor.
  key_insights_is_curated?: boolean;
  blueprint_enabled?: boolean;
  // True when `blueprint` is the admin-curated copy (customer_curated_content)
  // rather than the generated Stage 9 draft. Drives frontend precedence:
  // curated > per-customer config > generated draft.
  blueprint_is_curated?: boolean;
  // True when the authenticated viewer is an admin; gates admin-only UI like
  // the blueprint curation editor. Absent/false for client users.
  viewer_is_admin?: boolean;
  // The series rebaselines in force at this run, oldest first (date, what
  // changed, the note). Empty for a run before any rebaseline.
  rebaselines?: SeriesRebaseline[];
}

export interface DSCustomerSummary {
  id: number;
  brand_name: string;
  /** framework_json.brand_display_name — often differs from brand_name
   *  ("Sunbit" vs "Sunbit - Dental (Merchant)"). Carried so the caller can
   *  identify its own customer without the server matching strings for it. */
  brand_display_name: string | null;
  /** Newest series-eligible run; null when every scored run is excluded. */
  latest_run_id: number | null;
  latest_run_at: string | null;
  /** How the row matched a `brand` filter: 0 brand_name exact, 1 display-name
   *  exact, 2 substring. Absent/2 when no brand filter was sent. */
  match_rank?: number;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

export async function listDeepSignalCustomers(
  opts: { brand?: string; customerId?: number } = {},
): Promise<{ customers: DSCustomerSummary[] }> {
  const params = new URLSearchParams();
  // customerId is the unambiguous filter; brand is a name, and a name can
  // answer for more than one customer. The server ranks exact matches first
  // either way, and the user_brands scope applies to both.
  if (opts.customerId != null) params.set("customer_id", String(opts.customerId));
  else if (opts.brand) params.set("brand", opts.brand);
  const qs = params.toString();
  return apiFetch(`/deep-signal/customers${qs ? `?${qs}` : ""}`);
}

export async function listDeepSignalRuns(customerId: number): Promise<{
  customer_id: number;
  brand_name: string;
  runs: DSRun[];
  /** Every applied rebaseline of the series, oldest first. */
  rebaselines?: SeriesRebaseline[];
}> {
  return apiFetch(`/deep-signal/customers/${customerId}/runs`);
}

export async function getDeepSignalData(
  customerId: number,
  runId: number,
): Promise<DeepSignalData> {
  return apiFetch(`/deep-signal/customers/${customerId}/runs/${runId}`);
}

// Save the admin-curated blueprint for a customer. Admin only (backend
// enforces); the curated copy wins over the generated draft in the payload.
export async function saveCuratedBlueprint(
  customerId: number,
  blueprint: Blueprint,
): Promise<{ ok: boolean; blueprint: Blueprint }> {
  return apiFetch(`/deep-signal/customers/${customerId}/curated/blueprint`, {
    method: "PUT",
    body: JSON.stringify(blueprint),
  });
}

// Save the admin-curated Key Insights cards for a customer. Admin only (backend
// enforces); the curated cards win over the generated/config content in the
// payload. Returns the server-sanitized cards.
export async function saveCuratedKeyInsights(
  customerId: number,
  cards: KeyInsight[],
): Promise<{ ok: boolean; key_insights: KeyInsight[] }> {
  return apiFetch(`/deep-signal/customers/${customerId}/curated/key-insights`, {
    method: "PUT",
    body: JSON.stringify({ cards }),
  });
}

// Delete the admin-curated Key Insights row, restoring the generated/default
// content. Admin only; idempotent (a no-op when nothing is curated).
export async function deleteCuratedKeyInsights(
  customerId: number,
): Promise<{ ok: boolean; deleted: boolean }> {
  return apiFetch(`/deep-signal/customers/${customerId}/curated/key-insights`, {
    method: "DELETE",
  });
}
